import { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../hooks';
import { setRewards } from '../slices/fightSlice';
import Events from '../util/EventEmitter';
import { downloadBattleVideo, recordBattle } from '../util/battleVideo';
import useTranslator from './translate';

export function useBattlePlayback(fight: any) {
  const dispatch = useAppDispatch();
  const t = useTranslator();
  const pending = useRef(false);
  const recording = useRef<ReturnType<typeof recordBattle>>();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const started = Events.on('combatPlaybackStarted', (canvas: HTMLCanvasElement) => {
      if (!pending.current) return;
      pending.current = false;
      try {
        const capture = recordBattle(canvas);
        recording.current = capture;
        capture.video
          .then(downloadBattleVideo)
          .catch(() => setError('battle_video_failed'))
          .finally(() => {
            recording.current = undefined;
            setSaving(false);
          });
      } catch (err) {
        setSaving(false);
        setError(err instanceof Error && err.message === 'battle_video_unsupported' ? err.message : 'battle_video_failed');
      }
    });
    const finished = Events.on('combatPlaybackFinished', () => recording.current?.stop());
    const failed = Events.on('combatPlaybackFailed', () => {
      pending.current = false;
      recording.current?.cancel();
      setSaving(false);
      setError('battle_video_failed');
      dispatch(setRewards(fight?.rewards ?? {}));
    });
    return () => {
      Events.off(started);
      Events.off(finished);
      Events.off(failed);
      pending.current = false;
      recording.current?.cancel();
    };
  }, [fight, dispatch]);

  function replay(save = false) {
    if (!fight || saving) return;
    setError('');
    if (save && (!HTMLCanvasElement.prototype.captureStream || typeof MediaRecorder === 'undefined')) {
      setError('battle_video_unsupported');
      return;
    }
    pending.current = save;
    setSaving(save);
    dispatch(setRewards(null));
    // Reuse the recorded actions locally; never send another combat/reward request.
    Events.emit('setFightData', { ...fight, onFinish: () => {} });
  }

  return { replay: () => replay(), save: () => replay(true), saving, error: error ? t(error) : '' };
}
